// @ts-check
import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Input as DefaultInput,
  Button,
  ButtonStyledButton
} from '@codex/basics';
import { Icon } from '@codex/icons';
import styled from 'styled-components';
import { debounce } from '@codex/helpers';

import SuggestionsPopup from './SuggestionsPopup/SuggestionsPopup';

const Input = styled(DefaultInput)``;

const StyledForm = styled.form`
  position: relative;
  width: 100%;

  @media ${({ theme }) => `${theme.breakpoints.desktop}`} {
    width: 382px;
  }
`;

const SearchFieldset = styled.div`
  display: flex;
  flex: 1;

  ${Input} {
    flex: 1;
    z-index: 99;

    input {
      border-color: ${({ theme }) => theme.name === 'brightworld' &&
        theme.colors.header.nav.searchBar.border};
      border-radius: 4px 0 0 4px;
      padding: 13px 40px 13px 20px;
      height: 100%;
      color: ${({ theme }) => theme.colors.header.nav.searchBar.text};
      box-sizing: border-box;
    }

    &:focus {
      border-color: ${({ theme }) => theme.colors.link.default};
    }

    ${Icon} {
      color: ${props => props.theme.colors.shades[4]};
    }
  }

  ${ButtonStyledButton} {
    z-index: 99;
    width: auto;
    position: relative;
    margin-left: -1px;
    border-radius: 0 4px 4px 0;
  }

  ${Button} {
    &:hover {
      background-color: ${({ theme }) => theme.colors.primary.default};
      border-color: ${({ theme }) => theme.colors.primary.default};
    }
  }
`;

// TODO: Move to @codex/helpers package
const useLatestFetch = (value, fetchFn, setFn) => {
  useEffect(() => {
    if (!value) {
      return;
    }
    let stillPresent = true;

    fetchFn(encodeURIComponent(value))
      // eslint-disable-next-line no-console
      .catch(e => console.error(e))
      .then((response) => {
        if (stillPresent) {
          setFn(response);
        }
      });

    return () => {
      stillPresent = false;
    };
  }, [value]);
};

/**
 * Debounce for async functions
 * Basically it returns a function which will always return the "next" promise
 * for future invocation of `asyncFn` (in the same time slice)
 *
 * ```
 * const search = (query) => fetch(`http://.../?q=${query}`)
 * const debouncedSearch = debounceAsync(search, 1000)
 *
 * debouncedSearch("ph")
 * // fetches just once, the resolved res is the same in both promises
 * // only the "phone" is used as a query
 * debouncedSearch("phone").then(res => ...)
 * ```
 *
 * @param asyncFn fetch fn which returns a promise (can take some arguments)
 * @param debounceOpts everything what @see debounce accepts
 */
const debounceAsync = (asyncFn, ...debounceOpts) => {
  let nextPromise;
  let resolve;
  let reject;
  let latestArgs;

  // save `resolve` & `reject` for later usage
  const prepareNextPromise = () => {
    nextPromise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
  };

  // call async function & start a new grace period
  const fire = async () => {
    try {
      resolve(await asyncFn(...latestArgs));
    } catch (e) {
      reject(e);
    }
    prepareNextPromise();
  };

  const debouncedFire = debounce(fire, ...debounceOpts);

  // prepare first period
  prepareNextPromise();

  // return a function which overrides arguments and always returns "next" promise
  return (...args) => {
    latestArgs = args;
    debouncedFire();
    return nextPromise;
  };
};

const HeaderSearchProduct = (props) => {
  const [query, setQuery] = useState('');
  const [highlightedSuggestionIndex, setHighlightedSuggestionIndex] = useState(
    -1
  );
  const [suggestions, setSuggestions] = useState([]);

  const fetchSuggestions = useMemo(
    () => debounceAsync((q) => {
      if (q.length === 0) {
        setSuggestions([]);
        setHighlightedSuggestionIndex(-1);
        return;
      }
      return props.getSuggestions(q);
    }, 300),
    []
  );

  useLatestFetch(query, fetchSuggestions, setSuggestions);

  const suggestionSelected = (enterKeyEvent = null) => {
    props.onSuggestionSelected(suggestions[highlightedSuggestionIndex]);

    if (suggestions[highlightedSuggestionIndex].type === 'sayt') {
      if (enterKeyEvent) {
        // stops submitting form
        enterKeyEvent.preventDefault();
      }

      const targetUrl = suggestions[highlightedSuggestionIndex].url;
      const fmoModalIntentEvent = document.createEvent('CustomEvent');
      fmoModalIntentEvent.initCustomEvent(
        'FMO_REDIRECT_MODAL_INTENT',
        true,
        true,
        {
          targetUrl: targetUrl,
          ifNotOpening: () => {
            window.location.href = targetUrl;
          }
        }
      );
      document.dispatchEvent(fmoModalIntentEvent);
    } else {
      setQuery(suggestions[highlightedSuggestionIndex].title);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (highlightedSuggestionIndex < suggestions.length - 1) {
        setHighlightedSuggestionIndex(highlightedSuggestionIndex + 1);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (highlightedSuggestionIndex > 0) {
        setHighlightedSuggestionIndex(highlightedSuggestionIndex - 1);
      }
    } else if (e.key === 'Enter') {
      if (highlightedSuggestionIndex >= 0) {
        suggestionSelected(e);
      } else {
        setSuggestions([]);
      }
    }
  };

  return (
    <StyledForm
      method="POST"
      action={props.searchAction}
      onSubmit={(e) => {
        props.onSubmit(e, query);
      }}
      className={props.className}
    >
      <SearchFieldset>
        <input name="subaction" type="hidden" value="keyword_search" />
        <input name="submitted_by" type="hidden" value="" />
        <Input
          attrs={{
            input: {
              'autoCapitalize': 'off',
              'autoCorrect': 'off',
              'aria-label': 'Search Field'
            }
          }}
          name="search-field"
          clearIcon
          placeholderText={props.placeholder}
          value={query}
          onClear={() => {
            setQuery('');
            setHighlightedSuggestionIndex(-1);
          }}
          onChange={(e) => {
            setHighlightedSuggestionIndex(-1);
            setSuggestions([]);
            setQuery(e.target.value);
          }}
          onKeyDown={e => handleKeyDown(e)}
          onBlur={() => setSuggestions([])}
          autoComplete="off"
        />
        <Button full type="submit">
          {props.btnTitle}
        </Button>
      </SearchFieldset>
      {suggestions?.length > 0 ? (
        <SuggestionsPopup
          suggestions={suggestions}
          highlightedSuggestionIndex={highlightedSuggestionIndex}
          currency={props.currency}
          onSuggestionItemMouseEnter={setHighlightedSuggestionIndex}
          onSuggestionItemClick={() => {
            suggestionSelected();
          }}
        />
      ) : null}
    </StyledForm>
  );
};

HeaderSearchProduct.propTypes = {
  btnTitle: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  currency: PropTypes.string,
  getSuggestions: PropTypes.func.isRequired,
  searchAction: PropTypes.string.isRequired,
  onSubmit: PropTypes.func,
  onSuggestionSelected: PropTypes.func,
  className: PropTypes.string
};

HeaderSearchProduct.defaultProps = {
  placeholder: '',
  currency: 'GBP',
  onSubmit: () => {},
  onSuggestionSelected: () => {},
  className: undefined
};

export default HeaderSearchProduct;
